import { ApolloError, gql } from "@apollo/client";
import { getUserFacingErrorMessage } from "@hex/common";
import React, { ReactNode, useCallback } from "react";

import { ContactAnAdmin } from "../../components/common/ContactAnAdmin";
import { ArrowTopRightIcon } from "../../components/icons/CustomIcons";
import { HexAnchorButton, Txt } from "../../hex-components";
import { ORG_ID } from "../../orgs";
import { Routes, useQueryParams } from "../../route/routes";

import { useAuthorizeCliDeviceMutation } from "./CliAuthFlow.generated";
import { CliAuthFlowScreen } from "./CliAuthFlowScreen";

gql`
  mutation AuthorizeCliDevice($userCode: String!, $action: CliDeviceAction!) {
    authorizeCliDevice(userCode: $userCode, action: $action) {
      action
    }
  }
`;

export const CliAuthFlow: React.ComponentType = React.memo(
  function CliAuthFlow() {
    const [authorizeCliDevice, authorizeCliDeviceResult] =
      useAuthorizeCliDeviceMutation();
    const authorizationError = authorizeCliDeviceResult.error;
    const authorizationState = authorizationError
      ? "DENY"
      : authorizeCliDeviceResult.data?.authorizeCliDevice.action;

    const onAuthorize = useCallback(
      async (userCode: string, approve: boolean) => {
        await authorizeCliDevice({
          variables: {
            userCode,
            action: approve ? "APPROVE" : "DENY",
          },
        });
      },
      [authorizeCliDevice],
    );

    const userCodeFromUrl = useQueryParams().get("user_code");

    return (
      <>
        {!authorizationState && (
          <CliAuthFlowScreen.ConfirmUserCode
            userCode={userCodeFromUrl}
            onAuthorize={onAuthorize}
          />
        )}
        {authorizationState === "APPROVE" && <CliAuthFlowScreen.Authorized />}
        {authorizationState === "DENY" && (
          <CliAuthFlowScreen.NotAuthorized
            error={Boolean(authorizationError)}
            message={
              !authorizationError
                ? "You have denied CLI authorization. You can close this window."
                : unpackErrorMessage(authorizationError)
            }
          />
        )}
      </>
    );
  },
);

function unpackErrorMessage(error: ApolloError): ReactNode | string {
  const msg = getUserFacingErrorMessage(error);
  if (!msg) return "An unknown error occurred.";

  const msgLines = msg.split("\n");
  const msgLineText = (
    <Txt>
      {msgLines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < msgLines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </Txt>
  );
  if (msg?.match(/Re-enable (CLI|API) access in 'Settings > Integrations'/)) {
    return (
      <>
        {msgLineText}
        <HexAnchorButton
          css="align-self: end;"
          href={Routes.SETTINGS.href(ORG_ID, true, { subView: "integrations" })}
          icon={<ArrowTopRightIcon />}
          intent="primary"
        >
          Settings
        </HexAnchorButton>
      </>
    );
  } else if (msg?.match(/Contact a workspace admin/)) {
    return (
      <>
        {msgLineText}
        <ContactAnAdmin button="default" css="align-self: end;" />
      </>
    );
  } else {
    return msgLineText;
  }
}
